import React, { FC, MouseEventHandler, useCallback } from 'react';
import { JourneyMenu, JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { Journey } from 'src/common/interfaces/journey.interface';
import { DashboardHomeActionPanelTypes } from '../card-options';
import { useReactConstructor } from 'src/utils/react/constructor.hook';
import {
  AddStrongIcon,
  AnalyticsRegularIcon,
  ChevronRightRegularIcon,
  CopyRegularIcon,
  DeleteRegularIcon,
  NotificationsRegularIcon,
  RightRegularIcon,
  ShareRegularIcon,
  PreviewRegularIcon,
  LinkRegularIcon,
  ConvertRegularIcon,
  UserRegularIcon,
} from 'src/monet/icons';
import { LockClosedIcon } from '@heroicons/react/outline';

import OrganizationLink from 'src/common/OrganizationLink';
import { useJourneys } from 'src/store/journeys';
import { shallow } from 'zustand/shallow';
import { FreeGatingEnum, useFreeGatingStore } from 'src/common/free-gating/store';
import classNames from 'classnames';
import { FreeGatingTooltip } from 'src/common/free-gating/tooltip';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentSubscription } from 'src/store/subscription';
import { useCurrentUser } from 'src/store/user';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { isJourneyAccessible } from 'src/utils/journey-access';

interface DashboardHomeJourneysSettingsMenuProps extends Pick<JourneyMenuPropsInterface, 'openRef' | 'closeRef'> {
  journey: Journey;
  renderMenuOrigin: (onClick: any) => any;
  onOpen: () => void;
  onClose: () => void;
  onItemClicked: (action: DashboardHomeActionPanelTypes) => void;
  isJourneyAccessible?: boolean;
  onRestrictedOperation?: (operationName: string) => void;
}

export const DashboardHomeJourneysSettingsMenu: FC<DashboardHomeJourneysSettingsMenuProps> = ({
  journey,
  renderMenuOrigin,
  openRef,
  closeRef,
  onOpen,
  onClose,
  onItemClicked,
  isJourneyAccessible = true,
  onRestrictedOperation,
}) => {
  const { currentOrganization } = useCurrentOrganization();
  const { currentUser } = useCurrentUser();
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { features, showUpgradeModal } = useFeatureRestrictions();
  const journeys = useJourneys((state) => state.journeys, shallow);
  const { isFreeOrg, hasReachedFreeLimit } = useJourneys((state) => ({
    isFreeOrg: state.isFreeOrg,
    hasReachedFreeLimit: state.hasReachedFreeLimit,
  }), shallow);
  const { isStarter, isPro, isEnterprise } = useCurrentSubscription();
  const setFreeGatingModalByType = useFreeGatingStore((state) => state.setFreeGatingModalByType);

  const currentUserUUID = currentUser?.uuid;

  const menuItemClasses = 'text-bedrock-p text-bedrock-black text-left';

  const onMenuClicked: MouseEventHandler = () => {
    onOpen();
  };

  // Check if user is on Start plan and journey is not accessible
  const isStartPlanWithLockedJourney = isStarter && !isJourneyAccessible;

  const onJourneyMenuItemClicked = useCallback(
    (action: DashboardHomeActionPanelTypes) => {
      return () => {
        // Always allow delete operations
        if (action === 'delete') {
          return onItemClicked(action);
        }

        // For Start plan users with locked journeys, show upgrade modal for all operations except delete
        if (isStartPlanWithLockedJourney) {
          const operationNames: Record<DashboardHomeActionPanelTypes, string> = {
            insights: 'view insights for',
            status: 'change status of',
            social_sharing: 'configure social sharing for',
            share: 'share',
            duplicate: 'duplicate',
            notifications: 'configure notifications for',
            'create-template': 'create template from',
            move: 'move',
            conversations: 'configure conversations for',
            tags: 'manage tags for',
            'organization-templates': 'convert to organization template',
            'update-ownership': 'change ownership of',
            delete: 'delete',
          };
          
          if (onRestrictedOperation) {
            onRestrictedOperation(operationNames[action] || 'perform this operation on');
          } else {
            showUpgradeModal('unlimited_journeys', 'Upgrade to Grow plan to access all your journeys and perform this operation.');
          }
          return;
        }

        // Handle existing feature restrictions for other plans
        if (action === 'duplicate') {
          if ((isFreeOrg && hasReachedFreeLimit) || (!isFreeOrg && isStarter && hasReachedFreeLimit)) {
            setFreeGatingModalByType(FreeGatingEnum.JOURNEYS, true, true);
            return;
          }
        }

        if (action === 'organization-templates') {
          if (!features.canAccessOrganizationTemplates) {
            showUpgradeModal('organization_templates', 'Upgrade to Scale plan to convert journeys to organization templates.');
            return;
          }
        }

        if (action === 'tags') {
          if (!features.canAccessTagsAndFilters) {
            showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to manage tags on your journeys.');
            return;
          }
        }

        return onItemClicked(action);
      };
    },
    [isStartPlanWithLockedJourney, isFreeOrg, isStarter, hasReachedFreeLimit, isPro, isEnterprise, features.canAccessTagsAndFilters, showUpgradeModal, onRestrictedOperation]
  );

  // Helper function to render menu item with optional lock icon and arrow
  const renderMenuItem = (
    action: DashboardHomeActionPanelTypes,
    icon: React.ReactNode,
    label: string,
    showArrow: boolean = false,
    showLock: boolean = false
  ) => (
    <JourneyMenu.Item 
      className={classNames(menuItemClasses, {
        'opacity-60': showLock,
      })}
      onClick={onJourneyMenuItemClicked(action)}
    >
      <div className='flex items-center justify-between w-full'>
        <div className='flex items-center space-x-sm'>
          {icon}
          <span className='text-bedrock-p text-bedrock-black'>{label}</span>
        </div>
        <div className='flex items-center space-x-sm'>
          {showLock && <LockClosedIcon className='w-4 h-4 text-gray-400' />}
          {showArrow && <ChevronRightRegularIcon className='w-4 h-4 text-gray-400' />}
        </div>
      </div>
    </JourneyMenu.Item>
  );

  return (
    <JourneyMenu
      placement='bottom-start'
      openRef={openRef}
      closeRef={closeRef}
      onOpen={onOpen}
      onClose={onClose}
      renderOrigin={() => renderMenuOrigin(onMenuClicked)}
      containerClassName='w-[300px]'
    >
      {renderMenuItem(
        'insights',
        <AnalyticsRegularIcon />,
        'Insights',
        false,
        isStartPlanWithLockedJourney
      )}
      {renderMenuItem(
        'status',
        <PreviewRegularIcon />,
        'Status',
        true,
        isStartPlanWithLockedJourney
      )}
      {renderMenuItem(
        'social_sharing',
        <LinkRegularIcon />,
        'Social sharing',
        true,
        isStartPlanWithLockedJourney
      )}
      {renderMenuItem(
        'update-ownership',
        <UserRegularIcon />,
        'Change ownership',
        false,
        isStartPlanWithLockedJourney || journey.creator.uuid !== currentUserUUID
      )}
      {renderMenuItem(
        'share',
        <ShareRegularIcon />,
        'Share',
        false,
        isStartPlanWithLockedJourney
      )}
      <JourneyMenu.Item
        className={classNames(menuItemClasses, {
          'opacity-80': (isFreeOrg && hasReachedFreeLimit) || (!isFreeOrg && isStarter && hasReachedFreeLimit),
        })}
        onClick={onJourneyMenuItemClicked('duplicate')}
      >
        <FreeGatingTooltip placement='left'>
          <div className='flex items-center space-x-sm'>
            <CopyRegularIcon />
            <span className='text-bedrock-p text-bedrock-black'>Duplicate</span>
          </div>
        </FreeGatingTooltip>
      </JourneyMenu.Item>
      {renderMenuItem(
        'notifications',
        <NotificationsRegularIcon />,
        'Notifications',
        true,
        isStartPlanWithLockedJourney
      )}
      <JourneyMenu.Item className={menuItemClasses} onClick={onJourneyMenuItemClicked('delete')}>
        <div className='flex items-center space-x-sm'>
          <DeleteRegularIcon />
          <span className='text-bedrock-p text-bedrock-black'>Delete</span>
        </div>
      </JourneyMenu.Item>
      {renderMenuItem(
        'organization-templates',
        <ConvertRegularIcon />,
        'Convert to Organization Template',
        false,
        isStartPlanWithLockedJourney
      )}
      {renderMenuItem(
        'move',
        <RightRegularIcon />,
        'Move',
        true,
        isStartPlanWithLockedJourney
      )}
      {renderMenuItem(
        'tags',
        <AddStrongIcon />,
        'Tags',
        true,
        isStartPlanWithLockedJourney
      )}
    </JourneyMenu>
  );
};
